"use client";

import React, { useMemo } from "react";
import Image from "next/image";
import Chip from "@/components/global/ui/Chip";
import DecorationLine from "@/components/global/ui/DecorationLine";
import DecorationSquares from "@/components/global/ui/DecorationSquares";
import { cn, formatValue } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTACornerButton } from "@/shadcn/ui/corner-button";
import { useMixedTopUsers } from "@/hooks/useLeaderboardData";

import { PageLeaderboardsSchema } from "@/sanity/pages/PageLeaderboardsTemplate";

import bitunix from "@/public/leaderboards/1.svg";
import pionex from "@/public/leaderboards/3.svg";
import phemex from "@/public/leaderboards/2.svg";

const exchanges = {
  bitunix: {
    name: "EXC 1",
    color: "#D9F9B9",
    logo: bitunix,
  },
  phemex: {
    name: "EXC 3",
    color: "#0B5BE0",
    logo: phemex,
  },
  pionex: {
    name: "EXC 2",
    color: "#FF8D28",
    logo: pionex,
  },
};

const exchangesList = Object.values(exchanges);

export default function HeroSection({
  data,
  className,
}: {
  data: PageLeaderboardsSchema;
  className?: string;
}) {
  // Fetch mixed top users (20 users interleaved from all exchanges)
  const { data: mixedUsers, isLoading } = useMixedTopUsers(20);

  // Create slider data - duplicate for infinite scroll effect
  const sliderUsers = useMemo(() => {
    if (!mixedUsers || mixedUsers.length === 0) {
      // Fallback: create static data if API not loaded
      return Array.from({ length: 12 }).map((_, index) => ({
        name: "Loading...",
        avatarURL: "https://cdn.discordapp.com/embed/avatars/0.png",
        tradingVolume: 0,
        cashback: 0,
        position: index + 1,
        exchange: exchangesList[index % exchangesList.length].name,
        exchangeColor: exchangesList[index % exchangesList.length].color,
        logo: exchangesList[index % exchangesList.length].logo,
      }));
    }

    // Add logo to each user based on exchange name
    return mixedUsers.map((user) => ({
      ...user,
      logo:
        exchanges[user.exchange.toLowerCase() as keyof typeof exchanges]
          ?.logo || bitunix,
    }));
  }, [mixedUsers]);

  const heroArrayLength = sliderUsers.length;

  return (
    <section className={cn("relative overflow-clip pb-[3rem]", className)}>
      <DecorationSquares className="top-[2px]" />

      <div className="relative py-[6rem] lg:pt-[8rem]">
        <DecorationLine
          className="top-0"
          orientation="vertical"
          position="left"
        />
        <DecorationLine
          className="top-0"
          orientation="vertical"
          position="right"
        />
        <DecorationLine className="left-0 bottom-0" orientation="horizontal" />

        <div className="container relative flex flex-col items-center">
          <Chip
            className="prose-img:!w-[3rem]"
            image={data?.heroChip?.icon}
            text={data?.heroChip?.text}
          />

          {data?.heroTitle && (
            <div className="richtext prose-p:w-[min(65rem,100%)]">
              <PortableText
                value={data.heroTitle}
                components={richTextComponent}
              />
            </div>
          )}

          {data?.heroCTA && (
            <CTACornerButton
              className="mt-[4rem] sm:min-w-[50rem]"
              data={data.heroCTA}
            />
          )}

          <p className="font-geistmono text-[1.75rem] lg:text-[1.875rem] uppercase text-black/60 mt-[2rem] mb-[1rem] text-center w-full">
            {data?.settings?.language === "en"
              ? "$210,000 is waiting in the leaderboard this month."
              : "210.000 $ warten diesen Monat im Leaderboard."}
          </p>

          <div className="flex justify-center items-center gap-[1rem]">
            <div className="flex justify-center items-center gap-[1rem] px-[2rem] py-[1rem] rounded-full border border-black/10">
              🥇
              <span className="font-geistmono text-[2rem] lg:text-[2.25rem] text-black/70">
                $15.000
              </span>
            </div>
            <div className="flex justify-center items-center gap-[1rem] px-[2rem] py-[1rem] rounded-full border border-black/10">
              🥈
              <span className="font-geistmono text-[2rem] lg:text-[2.25rem] text-black/70">
                $10.000
              </span>
            </div>
            <div className="flex justify-center items-center gap-[1rem] px-[2rem] py-[1rem] rounded-full border border-black/10">
              🥉
              <span className="font-geistmono text-[2rem] lg:text-[2.25rem] text-black/70">
                $8.000
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="relative overflow-clip py-[3rem]">
        <DecorationLine className="left-0 bottom-0" orientation="horizontal" />

        {/* side fades */}
        <div className="hidden lg:block absolute z-[1] left-0 top-0 w-1/3 h-full bg-gradient-to-r from-[#F3F2F1]/90 to-transparent pointer-events-none" />
        <div className="hidden lg:block absolute z-[1] right-0 top-0 w-1/3 h-full bg-gradient-to-l from-[#F3F2F1]/90 to-transparent pointer-events-none" />
        {/* end side fades */}

        <div
          className="flex will-change-transform"
          style={{
            width: `calc(${heroArrayLength * 2 * 40}rem + ${heroArrayLength * 2 * 1.5}rem)`,
            animation: `scrollLeft ${heroArrayLength * 3}s linear infinite`,
          }}
        >
          {[...sliderUsers, ...sliderUsers].map((user, index) => {
            return (
              <div
                key={index}
                className="ml-[1.5rem] bg-white px-[2.5rem] pt-[2.5rem] min-h-[50.625rem] w-[40rem] flex-shrink-0 flex flex-col items-start justify-between relative select-none"
                style={{
                  borderBottom: `2px solid ${user.exchangeColor}`,
                }}
              >
                <div className="flex items-center gap-[2rem]">
                  <Image
                    className="!w-[6.75rem] flex-shrink-0"
                    src={user.logo}
                    alt="logo"
                  />

                  <div className="flex flex-col items-start gap-[.5rem]">
                    <p className="mb-0 leading-none font-geistmono text-[2rem] text-black uppercase">
                      {user.name}
                    </p>
                    <p className="mb-0 leading-none font-geistmono text-[2rem] text-black/40 uppercase">
                      {user.exchange === "Bitunix" ? "Exchange 1" : user.exchange === "Pionex" ? "Exchange 2" : "Exchange 3"}
                    </p>
                  </div>
                </div>

                <div className="w-full flex flex-col">
                  {/* cashback */}
                  <div className="flex items-center justify-between gap-[2rem] mb-[1.5rem] pb-[1rem] border-b border-b-[#000000]/10">
                    <p className="mb-0 leading-none font-medium text-[4rem]">
                      ${formatValue(user.cashback, 0)}
                    </p>
                    <p className="mb-0 leading-none text-[2.25rem] text-black/60">
                      Cashback
                    </p>
                  </div>

                  {/* volume */}
                  <div className="flex items-center justify-between gap-[2rem] mb-[4rem]">
                    <p className="mb-0 leading-none text-[2.25rem]">
                      ${formatValue(user.tradingVolume, 0)}
                    </p>
                    <p className="mb-0 leading-none text-[2.25rem] text-black/60">
                      Volume
                    </p>
                  </div>

                  {/* card footer */}
                  <div
                    className={cn(
                      "inline-flex mx-auto gap-[1rem] px-[1.5rem] pb-[1rem] pt-[calc(1rem+2px)] font-geistmono",
                      "[clip-path:polygon(1.5rem_0,0_1.5rem,0_100%,100%_100%,100%_1.5rem,calc(100%-1.5rem)_0)]",
                    )}
                    style={{
                      background: user.exchangeColor,
                    }}
                  >
                    <p className="mb-0 leading-none font-geistmono text-[2rem] text-[#0A1A1D] uppercase font-medium">
                      {user.exchange === "Bitunix" ? "EXC 1" : user.exchange === "Pionex" ? "EXC 2" : "EXC 3"}
                    </p>
                    <div className="w-[2px] bg-[#0A1A1D]/20" />
                    <p className="mb-0 leading-none font-geistmono text-[2rem] text-[#0A1A1D] uppercase font-medium truncate max-w-[20rem]">
                      {user.name}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
